import { Link } from "react-router-dom";
import {
  Calculator, FileText, Share2, ShieldCheck, TrendingUp, Wrench, Users,
  CalendarClock, MapPinned, Store, CheckCircle2, XCircle, ArrowRight, Gauge,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { brl } from "@/lib/format";

const HERO_IMG = "https://images.unsplash.com/photo-1561400555-786780284b67?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHw0fHxjb21tZXJjaWFsJTIwcmVmcmlnZXJhdGlvbiUyMEhWQUMlMjB0ZWNobmljaWFuJTIwd29ya2luZyUyMHJlcGFpcnxlbnwwfHx8fDE3ODkzMjY4ODl8MA&ixlib=rb-4.1.0&q=85";

const PAINS = [
  "Cobra só a mão de obra e esquece peças, deslocamento e impostos",
  "Faz orçamento no papel ou no WhatsApp e perde tempo",
  "Não sabe a margem real de cada serviço",
  "Perde o histórico dos equipamentos e clientes",
  "Esquece de cobrar a manutenção preventiva",
];

const FEATURES = [
  { icon: Calculator, title: "Calculadora de preço", desc: "Hora técnica, km, peças, impostos, urgência e margem — o preço certo em menos de 2 minutos." },
  { icon: FileText, title: "Orçamento profissional em PDF", desc: "Documento com sua marca, condições, garantia e validade. Pronto para impressionar o cliente." },
  { icon: Share2, title: "Envio pelo WhatsApp", desc: "Mensagem automática e profissional com os dados do orçamento. Um toque e enviado." },
  { icon: Wrench, title: "Ordens de serviço", desc: "OS completa com diagnóstico, peças, garantia e assinatura. Organize a execução." },
  { icon: Users, title: "Clientes e equipamentos", desc: "CRM simples com histórico completo de cada cliente e de cada equipamento." },
  { icon: CalendarClock, title: "Manutenção preventiva", desc: "Alertas de manutenções próximas e atrasadas. Nunca mais perca um retorno." },
  { icon: MapPinned, title: "Referências regionais (Beta)", desc: "Mediana de preços por cidade e serviço, formada por profissionais participantes — sem expor ninguém." },
  { icon: Store, title: "Diretório de profissionais", desc: "Perfil público opcional para clientes da sua região encontrarem você." },
];

const FAQ = [
  { q: "Preciso pagar para começar?", a: "Não. Você começa grátis e ainda ganha 7 dias de teste do plano Profissional, sem informar cartão. Depois escolhe se quer continuar no Grátis ou assinar." },
  { q: "O GEPR define o preço por mim?", a: "Não. O GEPR calcula com base nos seus custos, sua margem e suas configurações. As referências regionais são estatísticas agregadas e informativas — a decisão final é sempre sua." },
  { q: "As referências regionais mostram o preço de algum concorrente?", a: "Nunca. Os dados são anonimizados e agregados (mediana e média aparada). Nenhum preço individual, profissional ou empresa é identificável." },
  { q: "Funciona no celular?", a: "Sim, o GEPR foi feito mobile-first: botões grandes, poucos campos por tela e navegação rápida — pensado para quem trabalha no campo, com o celular em uma mão." },
  { q: "Meus dados estão seguros?", a: "Sim. Cada usuário acessa apenas os próprios dados, com autenticação segura e estrutura compatível com a LGPD. Você pode exportar ou excluir seus dados." },
  { q: "Posso cancelar quando quiser?", a: "Sim. Sem fidelidade. Ao cancelar, sua conta volta para o plano Grátis e seus dados continuam com você." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <header className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur-md text-white border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Logo light />
          <nav className="hidden md:flex items-center gap-6 text-sm text-slate-300">
            <a href="#funcionalidades" className="hover:text-white transition-colors">Funcionalidades</a>
            <a href="#referencias" className="hover:text-white transition-colors">Referências regionais</a>
            <a href="#planos" className="hover:text-white transition-colors">Planos</a>
            <a href="#faq" className="hover:text-white transition-colors">FAQ</a>
            <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
            <Link to="/diretorio" className="hover:text-white transition-colors">Diretório</Link>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button variant="ghost" className="text-slate-200 hover:text-white hover:bg-slate-800 h-11" data-testid="header-login-btn">Entrar</Button>
            </Link>
            <Link to="/cadastro">
              <Button className="bg-sky-600 hover:bg-sky-500 text-white h-11 px-5 active:scale-[0.98] transition-transform" data-testid="header-cta-btn">
                Começar grátis
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="bg-slate-900 text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-in-up">
            <p className="text-xs font-mono-num uppercase tracking-widest text-sky-400 font-semibold mb-4">
              Software para técnicos de refrigeração
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Pare de perder dinheiro nos seus serviços de refrigeração.
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl">
              Calcule o preço certo, monte orçamentos profissionais e organize seus serviços em poucos minutos.
              Precifique melhor. Gerencie seus serviços. Faça sua empresa crescer.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/cadastro" className="w-full sm:w-auto">
                <Button className="w-full sm:w-auto h-13 h-14 px-8 text-base bg-sky-600 hover:bg-sky-500 text-white active:scale-[0.98] transition-transform" data-testid="hero-cta-btn">
                  Começar grátis <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/login" className="w-full sm:w-auto">
                <Button variant="outline" className="w-full sm:w-auto h-14 px-8 text-base border-slate-600 text-slate-200 hover:bg-slate-800 hover:text-white" data-testid="hero-demo-btn">
                  Testar agora (demo)
                </Button>
              </Link>
            </div>
            <p className="mt-4 text-xs text-slate-400">7 dias de plano Profissional grátis · Sem cartão · Cancele quando quiser</p>
            <div className="mt-8 flex flex-wrap gap-2">
              {["Calculadora de margem", "PDF profissional", "Envio por WhatsApp", "Ordem de serviço"].map((b) => (
                <span key={b} className="text-xs font-medium bg-slate-800 border border-slate-700 rounded-full px-3 py-1.5 text-slate-300">{b}</span>
              ))}
            </div>
          </div>
          <div className="relative hidden lg:block">
            <div className="absolute -inset-4 border border-slate-700 rounded-xl" />
            <img src={HERO_IMG} alt="Técnico de refrigeração trabalhando em unidade comercial"
                 className="rounded-lg shadow-2xl object-cover aspect-[4/3] w-full" loading="eager" />
            <div className="absolute -bottom-6 -left-6 bg-white text-slate-900 rounded-lg shadow-xl p-4 border border-slate-200">
              <p className="text-xs font-mono-num uppercase tracking-widest text-slate-500">Margem do serviço</p>
              <p className="font-mono-num text-2xl font-semibold text-green-600">+32,4%</p>
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEMA */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-mono-num uppercase tracking-widest text-sky-700 font-semibold">O problema</p>
          <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight max-w-2xl">
            Você sabe exatamente quanto está lucrando em cada serviço?
          </h2>
          <p className="mt-3 text-slate-600 max-w-2xl">
            A maioria dos técnicos cobra no feeling — e descobre o prejuízo só no fim do mês.
          </p>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {PAINS.map((p) => (
              <div key={p} className="bg-white border border-slate-200 rounded-lg p-5 flex gap-3 shadow-sm">
                <XCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
                <p className="text-sm text-slate-700">{p}</p>
              </div>
            ))}
            <div className="bg-slate-900 text-white border border-slate-800 rounded-lg p-5 flex gap-3 shadow-sm">
              <CheckCircle2 className="h-5 w-5 text-sky-400 shrink-0 mt-0.5" />
              <p className="text-sm">O GEPR resolve isso: custo, margem e preço final claros antes de enviar o orçamento.</p>
            </div>
          </div>
        </div>
      </section>

      {/* COMO FUNCIONA */}
      <section className="py-16 sm:py-24 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-mono-num uppercase tracking-widest text-sky-700 font-semibold">Como funciona</p>
          <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight">Do cálculo ao orçamento enviado em 4 passos</h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { n: "1", t: "Cadastre o cliente", d: "Cliente e equipamento ficam salvos com histórico completo." },
              { n: "2", t: "Informe o serviço", d: "Horas, técnicos, deslocamento, peças e urgência." },
              { n: "3", t: "Veja o preço certo", d: "Custo, impostos, margem e preço final — tudo transparente. Consulte a referência regional se quiser." },
              { n: "4", t: "Envie o orçamento", d: "PDF profissional com sua marca, direto pelo WhatsApp." },
            ].map((s) => (
              <div key={s.n} className="border border-slate-200 rounded-lg p-5 bg-slate-50">
                <span className="font-mono-num text-3xl font-semibold text-sky-600">{s.n}</span>
                <h3 className="mt-3 font-semibold text-lg">{s.t}</h3>
                <p className="mt-1 text-sm text-slate-600">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FUNCIONALIDADES */}
      <section id="funcionalidades" className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-mono-num uppercase tracking-widest text-sky-700 font-semibold">Funcionalidades</p>
          <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight">Tudo que sua operação precisa, em um só lugar</h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {FEATURES.map((f) => (
              <div key={f.title} className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm hover:shadow-md transition-shadow">
                <f.icon className="h-6 w-6 text-sky-600" />
                <h3 className="mt-3 font-semibold">{f.title}</h3>
                <p className="mt-1 text-sm text-slate-600">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DEMO VISUAL */}
      <section className="py-16 sm:py-24 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-mono-num uppercase tracking-widest text-sky-400 font-semibold">Transparência total</p>
            <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight">
              Agora imagine fazer isso em menos de 2 minutos.
            </h2>
            <p className="mt-4 text-slate-300 leading-relaxed">
              O motor de precificação mostra exatamente como o preço foi formado: mão de obra, deslocamento,
              peças, impostos e margem. Nada escondido. Se o preço ficar abaixo do custo, o sistema avisa na hora.
            </p>
            <ul className="mt-6 space-y-3">
              {["Alerta de prejuízo antes de enviar", "Adicionais de urgência e plantão configuráveis", "Valor mínimo de serviço garantido"].map((i) => (
                <li key={i} className="flex gap-3 text-sm text-slate-200">
                  <CheckCircle2 className="h-5 w-5 text-sky-400 shrink-0" /> {i}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 font-mono-num text-sm shadow-2xl">
            <p className="text-xs uppercase tracking-widest text-slate-400 mb-4">Resumo do cálculo · demonstração</p>
            {[["Mão de obra (4h × 1 téc.)", 480], ["Deslocamento (24 km)", 60], ["Peças", 1810], ["Outros custos", 80]].map(([l, v]) => (
              <div key={l} className="flex justify-between py-2 border-b border-slate-700 text-slate-300">
                <span>{l}</span><span>{brl(v)}</span>
              </div>
            ))}
            <div className="flex justify-between py-2 border-b border-slate-700 text-slate-300">
              <span>Custo total</span><span className="text-white font-semibold">{brl(2430)}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-slate-700 text-slate-300">
              <span>Impostos (6%)</span><span>{brl(145.8)}</span>
            </div>
            <div className="flex justify-between py-3 text-lg">
              <span className="font-semibold text-white">Preço final</span>
              <span className="font-semibold text-sky-400">{brl(3679.71)}</span>
            </div>
            <div className="flex justify-between text-green-400">
              <span>Lucro estimado</span><span>{brl(1103.91)} · 30%</span>
            </div>
          </div>
        </div>
      </section>

      {/* PARA QUEM É + ANTES/DEPOIS */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12">
          <div>
            <p className="text-xs font-mono-num uppercase tracking-widest text-sky-700 font-semibold">Para quem é</p>
            <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight">Feito para quem vive de refrigeração</h2>
            <div className="mt-6 flex flex-wrap gap-2">
              {["Técnico autônomo", "Instalador de ar-condicionado", "Refrigeração comercial", "Câmaras frias",
                "Refrigeração industrial", "Empresas de manutenção", "Chillers e bombas de calor", "Pequenas empresas"].map((t) => (
                <span key={t} className="text-sm bg-white border border-slate-200 rounded-full px-4 py-2 shadow-sm">{t}</span>
              ))}
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
            <div className="grid grid-cols-2 text-sm font-semibold">
              <div className="p-4 bg-slate-100 text-slate-600">Antes</div>
              <div className="p-4 bg-sky-600 text-white">Com o GEPR</div>
            </div>
            {[
              ["Preço no feeling", "Preço calculado com margem real"],
              ["Orçamento no papel", "PDF profissional com sua marca"],
              ["Esquece custos e km", "Todos os custos entram no cálculo"],
              ["Cliente e equipamento soltos", "Histórico completo organizado"],
              ["Preventiva esquecida", "Alertas de manutenção automáticos"],
            ].map(([a, b]) => (
              <div key={a} className="grid grid-cols-2 text-sm border-t border-slate-200">
                <div className="p-4 text-slate-500 flex gap-2"><XCircle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />{a}</div>
                <div className="p-4 text-slate-800 flex gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 shrink-0 mt-0.5" />{b}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REFERÊNCIAS REGIONAIS */}
      <section id="referencias" className="py-16 sm:py-24 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-xs font-mono-num uppercase tracking-widest text-amber-600 font-semibold bg-amber-50 border border-amber-200 rounded-full px-3 py-1">Beta controlado</span>
            <h2 className="mt-4 text-2xl sm:text-3xl font-bold tracking-tight">
              Referências de preço por cidade e região
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Tenha referências de preços por cidade e região para tomar decisões melhores, sem abrir mão do seu
              próprio critério profissional. Os valores são estatísticas agregadas — mediana e média aparada —
              formadas por contribuições anonimizadas de profissionais participantes.
            </p>
            <ul className="mt-6 space-y-3 text-sm text-slate-700">
              {[
                "Nenhum preço individual ou profissional é identificável",
                "Referência exibida só com contribuições mínimas suficientes",
                "Você aceita, ajusta ou ignora — nunca substituímos sua configuração",
                "Indicamos quantidade de contribuições e data de atualização",
              ].map((i) => (
                <li key={i} className="flex gap-3"><ShieldCheck className="h-5 w-5 text-sky-600 shrink-0" />{i}</li>
              ))}
            </ul>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 shadow-sm">
            <p className="text-xs font-mono-num uppercase tracking-widest text-slate-500">Exemplo de referência · demonstração</p>
            <h3 className="mt-2 font-semibold text-lg">Instalação de split 12.000 BTUs — São Paulo/SP</h3>
            <div className="mt-4 flex items-end gap-6">
              <div>
                <p className="text-xs text-slate-500">Faixa recomendada</p>
                <p className="font-mono-num text-2xl font-semibold text-slate-900">R$ 520 – R$ 690</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Mediana</p>
                <p className="font-mono-num text-xl font-semibold text-sky-700">R$ 580</p>
              </div>
            </div>
            <p className="mt-4 text-xs text-slate-500">
              Baseada em 6 contribuições (dados fictícios de demonstração), atualizada recentemente.
              Referência informativa e não vinculante.
            </p>
          </div>
        </div>
      </section>

      {/* DIRETÓRIO */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-mono-num uppercase tracking-widest text-sky-700 font-semibold">Opcional e modular</p>
            <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight">Diretório de profissionais</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Ative seu perfil público e seja encontrado por clientes da sua região. Você controla a visibilidade,
              os dados exibidos e pode desativar quando quiser. O GEPR apenas facilita o contato — orçamento,
              preço e garantia são sempre entre você e o cliente.
            </p>
            <Link to="/diretorio" className="inline-flex items-center gap-2 mt-6 text-sky-700 font-medium hover:underline" data-testid="directory-link">
              Ver diretório público <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-lg bg-slate-900 flex items-center justify-center">
                <Wrench className="h-6 w-6 text-sky-400" />
              </div>
              <div>
                <p className="font-semibold">RefriDemo Refrigeração</p>
                <p className="text-xs text-slate-500">São Paulo/SP · Câmaras frias, balcões, expositores</p>
              </div>
            </div>
            <p className="mt-4 text-sm text-slate-600">
              Atendimento comercial e industrial. Manutenção preventiva e corretiva com relatório técnico.
            </p>
            <p className="mt-3 text-xs text-slate-400">Exemplo fictício de perfil no diretório</p>
          </div>
        </div>
      </section>

      {/* PLANOS */}
      <section id="planos" className="py-16 sm:py-24 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-mono-num uppercase tracking-widest text-sky-400 font-semibold">Planos</p>
          <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight">Comece grátis. Cresça quando fizer sentido.</h2>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {[
              { name: "Grátis", price: "R$ 0", feats: ["Até 10 clientes", "Até 10 orçamentos/mês", "Calculadora de preço", "Orçamento em PDF", "Referências regionais limitadas"] },
              { name: "Profissional", price: "R$ 49,90", rec: true, feats: ["Clientes e orçamentos ilimitados", "Ordens de serviço com PDF", "Manutenção preventiva", "Relatórios completos", "Referências regionais completas", "Perfil no diretório"] },
              { name: "Empresa", price: "R$ 99,90", feats: ["Tudo do Profissional", "Múltiplos técnicos", "Gestão de equipe", "Relatórios avançados", "Destaque no diretório", "Suporte prioritário"] },
            ].map((p) => (
              <div key={p.name} className={`rounded-xl p-6 border ${p.rec ? "bg-sky-600 border-sky-500 shadow-2xl scale-[1.02]" : "bg-slate-800 border-slate-700"}`}>
                {p.rec && <span className="text-xs font-semibold bg-white text-sky-700 rounded-full px-3 py-1">Recomendado</span>}
                <h3 className="mt-3 font-display font-bold text-xl">{p.name}</h3>
                <p className="mt-2 font-mono-num text-3xl font-semibold">{p.price}<span className="text-sm font-normal text-slate-300">/mês</span></p>
                <p className="text-xs text-slate-300 mt-1">Plano anual com 2 meses de desconto</p>
                <ul className="mt-5 space-y-2.5 text-sm">
                  {p.feats.map((f) => (
                    <li key={f} className="flex gap-2.5"><CheckCircle2 className="h-4.5 w-4.5 h-5 w-5 text-sky-300 shrink-0" />{f}</li>
                  ))}
                </ul>
                <Link to="/cadastro" className="block mt-6">
                  <Button className={`w-full h-12 ${p.rec ? "bg-white text-sky-700 hover:bg-slate-100" : "bg-sky-600 hover:bg-sky-500 text-white"}`} data-testid={`plan-${p.name.toLowerCase()}-btn`}>
                    {p.price === "R$ 0" ? "Começar grátis" : "Testar 7 dias grátis"}
                  </Button>
                </Link>
              </div>
            ))}
          </div>
          <p className="mt-6 text-xs text-slate-400">Preços de lançamento, sujeitos a alteração. Sem fidelidade.</p>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-16 sm:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-mono-num uppercase tracking-widest text-sky-700 font-semibold">Dúvidas frequentes</p>
          <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight">Perguntas e respostas</h2>
          <Accordion type="single" collapsible className="mt-8">
            {FAQ.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-slate-200">
                <AccordionTrigger className="text-left font-medium" data-testid={`faq-q-${i}`}>{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-600">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-16 sm:py-24 bg-sky-600 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Gauge className="h-10 w-10 mx-auto mb-4 opacity-80" />
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Seu próximo orçamento profissional está a 2 minutos.</h2>
          <p className="mt-4 text-sky-100">Cadastre-se grátis e faça seu primeiro orçamento ainda hoje.</p>
          <Link to="/cadastro">
            <Button className="mt-8 h-14 px-10 text-base bg-slate-900 hover:bg-slate-800 text-white active:scale-[0.98] transition-transform" data-testid="final-cta-btn">
              Criar minha conta grátis
            </Button>
          </Link>
        </div>
      </section>

      <footer className="bg-slate-900 text-slate-400 py-10 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row justify-between gap-6">
          <div>
            <Logo light />
            <p className="mt-3 text-sm max-w-sm">Gestão Estratégica para Profissionais de Refrigeração. Precifique melhor. Gerencie seus serviços. Faça sua empresa crescer.</p>
          </div>
          <div className="flex gap-8 text-sm">
            <div className="flex flex-col gap-2">
              <Link to="/blog" className="hover:text-white">Blog</Link>
              <Link to="/diretorio" className="hover:text-white">Diretório</Link>
              <Link to="/cadastro" className="hover:text-white">Criar conta</Link>
            </div>
            <div className="flex flex-col gap-2">
              <Link to="/termos" className="hover:text-white">Termos de Uso</Link>
              <Link to="/privacidade" className="hover:text-white">Privacidade</Link>
              <Link to="/login" className="hover:text-white">Entrar</Link>
            </div>
          </div>
        </div>
        <p className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 text-xs text-slate-500">
          © {new Date().getFullYear()} GEPR. Referências regionais são estatísticas agregadas e informativas, não uma tabela obrigatória de preços.
        </p>
      </footer>
    </div>
  );
}
